
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { ProductList } from './ProductList';
import { Sidebar } from './ui/Sidebar';
import { MobileFilters } from './ui/MobileFilters';
import { ViewToggle } from './ui/ViewToggle';
import { supabase } from '../lib/supabaseClient';
import { useParams } from 'react-router-dom';
import { getProductKeywords } from '../utils/dataFallbacks';

function normalizeKeyword(str = '') {
  return decodeURIComponent(str)
    .replace(/[-_]/g, ' ') // unify dash and underscore to space
    .replace(/[^a-zA-Z0-9 ]/g, '') // remove all special characters except space
    .toLowerCase()
    .trim();
}

async function fetchCategoryMetadata(keyword) {
  const normalizedKeyword = normalizeKeyword(keyword);

  // ✅ Return default metadata when keyword is empty
  if (!normalizedKeyword || normalizedKeyword === 'all' || normalizedKeyword.trim() === '') {
    return {
      name: 'All Products',
      slug: 'all',
      description: 'Browse all tech products from top brands and categories.',
      meta_keywords: ['tech', 'products', 'deals'],
    };
  }

  let { data, error } = await supabase
    .from('categories')
    .select('*')
    .ilike('slug', `%${normalizedKeyword}%`)
    .eq('is_active', true)
    .limit(1)
    .maybeSingle();

  if ((!data || Object.keys(data).length === 0) && !error) {
    const fallback = await supabase
      .from('categories')
      .select('*')
      .ilike('name', `%${normalizedKeyword}%`)
      .eq('is_active', true)
      .limit(1)
      .maybeSingle();
    data = fallback.data;
    error = fallback.error;
  }

  if (error) {
    console.warn(`Search fetch error: ${error.message}`);
    return null;
  }

  return data;
}

export function SearchPage() {
  const { keyword } = useParams();
  const currentKeyword = keyword || 'all';
  const normalizedKeyword = normalizeKeyword(currentKeyword);
  console.log('Normalized keyword:', normalizedKeyword);

  const [filters, setFilters] = React.useState({
    search: normalizedKeyword,
    category: 'all',
    productType: 'all',
    minPrice: '',
    maxPrice: '',
    stockStatus: '',
    sort: 'featured',
  });

  const [view, setView] = React.useState('grid');

  const { data: metadata, isLoading, error } = useQuery({
    queryKey: ['categoryMetadata', normalizedKeyword],
    queryFn: () => fetchCategoryMetadata(currentKeyword),
    enabled: !!normalizedKeyword && normalizedKeyword.trim() !== '',
  });

  React.useEffect(() => {
    if (metadata?.slug) {
      setFilters((prev) => ({
        ...prev,
        category: metadata.slug,
        search: '',
      }));
    } else {
      setFilters((prev) => ({
        ...prev,
        category: 'all',
        search: normalizedKeyword,
      }));
    }
  }, [normalizedKeyword, metadata]);

  const title = metadata?.meta_title || metadata?.name
    ? `${metadata.meta_title || metadata.name} - Best Deals & Reviews ${new Date().getFullYear()}`
    : `${currentKeyword} - Tech Deals & Reviews | TechieDay`;

  const description =
    metadata?.meta_description || metadata?.description ||
    `Find the best deals on ${currentKeyword}. Compare prices, read reviews, and discover top-rated tech products with real-time price tracking.`;

  const keywords = metadata?.meta_keywords 
    ? (Array.isArray(metadata.meta_keywords) ? metadata.meta_keywords.join(', ') : metadata.meta_keywords)
    : `${currentKeyword}, tech deals, price comparison, reviews, best prices, amazon deals`;

  const canonicalUrl = `https://www.techieday.com/search/${encodeURIComponent(currentKeyword)}`;
  const ogImage = metadata?.og_image || 'https://www.techieday.com/images/og-default.jpg';

  // JSON-LD Schema for Search Results
  const searchResultsSchema = {
    "@context": "https://schema.org",
    "@type": "SearchResultsPage",
    "mainEntity": {
      "@type": "ItemList",
      "name": `${currentKeyword} Search Results`,
      "description": description,
      "url": canonicalUrl
    },
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://www.techieday.com"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Search",
          "item": canonicalUrl
        }
      ]
    }
  };

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={keywords} />

        {/* Enhanced Social sharing tags */}
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="TechieDay" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={ogImage} />
        <meta name="twitter:site" content="@techieday" />

        {/* JSON-LD Schema */}
        <script type="application/ld+json">
          {JSON.stringify(searchResultsSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen dark:bg-slate-900">
        <header className="w-full" role="banner">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 sm:pt-8">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center bg-white dark:bg-dark-card rounded-lg shadow-sm hover:shadow-md transition-shadow px-4 sm:px-6 py-6 sm:py-8 gap-4 sm:gap-0">
              <div className="flex-1">
                <h1 className="text-xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                  {filters.search ? `${filters.search} - Best Deals & Reviews ${new Date().getFullYear()}` : 'Search Results'}
                </h1>
                <p className="mt-2 text-gray-600 dark:text-gray-300 text-sm sm:text-base">
                  {filters.search ? `Results for "${filters.search}"` : 'All Products'}
                  {metadata?.name && ` in ${metadata.name}`}
                </p>
                {/* Trust indicator */}
                <div className="mt-2 flex items-center gap-2 text-xs text-green-600 dark:text-green-400">
                  <span aria-hidden="true">✅</span>
                  <span>Amazon Verified Prices</span>
                  <span aria-hidden="true">•</span>
                  <span aria-hidden="true">🔔</span>
                  <span>Real-time Updates</span>
                </div>
              </div>
              <div className="flex-shrink-0">
                <ViewToggle view={view} onViewChange={setView} />
              </div>
            </div>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8" role="main">
          <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
            {/* Desktop Sidebar */}
            <aside className="hidden lg:block lg:flex-shrink-0" role="complementary" aria-label="Product filters">
              <Sidebar filters={filters} onFilterChange={setFilters} />
            </aside>
            
            {/* Main Content */}
            <section className="flex-grow min-w-0" aria-label="Product results">
              <ProductList filters={filters} view={view} />
            </section>
          </div>
        </main>

        {/* Mobile Filters */}
        <MobileFilters filters={filters} onFilterChange={setFilters} />
      </div>
    </>
  );
}
